// apps/prompts/src/components/PromptsSidebar.tsx
// Filter hub sidebar — mirrors template HTML sidebar pattern.
// Drives BrowsePage via URL search params.
import { AI_MODEL_LABELS } from '@neuroforge/shared'
import { useAuthStore } from '@neuroforge/shared/client'
import {
  cn,
  WaveSVG,
  AppIcon,
  IconHome,
  IconSearch,
  IconStar,
  IconUser,
  IconPackage,
  IconPencil,
  IconFilePlus,
  IconShield,
  IconSparkles,
  IconFlame,
  IconClock,
  IconSortAscending,
  IconBolt,
  Logo,
  AppSwitcher,
} from '@neuroforge/ui'
import { useQuery } from '@tanstack/react-query'
import { useMemo, useCallback } from 'react'
import { NavLink, useNavigate, useLocation, useSearchParams } from 'react-router'

import CategorySelect, { type CategoryOption } from './CategorySelect'
import TagTypeahead from './TagTypeahead'

import { useCollections } from '@/hooks/useCollections'
import { usePromptStats, useFavorites } from '@/hooks/usePrompts'
import { fetchPopularTags } from '@/lib/api-prompts'
import { getAccent, getAllCategories } from '@/lib/prompt-helpers'

// ─── Preset intentions (Batch 1 du template) ───
const PRESETS = [
  { label: 'Debug backend', search: 'debug NestJS backend', category: 'CODING' },
  { label: 'Réunion', search: 'résumé réunion actions', category: 'PRODUCTIVITY' },
  { label: 'LinkedIn', search: 'post LinkedIn expert', category: 'WRITING' },
  { label: 'Workflow Notion', search: 'workflow Notion automation', category: 'PRODUCTIVITY' },
  { label: 'Urbanisme', search: 'permis déclaration préalable PLU', category: 'ADMIN_LEGAL' },
  { label: 'Travaux maison', search: 'audit devis travaux maison', category: 'CONSTRUCTION' },
  {
    label: 'Assistant dirigeant',
    search: 'assistant direction entrepreneur',
    category: 'EXECUTIVE_ASSISTANT',
  },
  { label: 'Prospection', search: 'prospection email B2B', category: 'SALES' },
  { label: 'Apprendre IA', search: 'apprentissage IA débutant', category: 'EDUCATION' },
]

const SORT_OPTIONS: CategoryOption[] = [
  { value: 'relevance', label: 'Pertinence', icon: IconSparkles },
  { value: 'popular', label: 'Popularité', icon: IconFlame },
  { value: 'recent', label: 'Récent', icon: IconClock },
  { value: 'alpha', label: 'A → Z', icon: IconSortAscending },
]

export default function PromptsSidebar() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { isAuthenticated, user } = useAuthStore()
  const { total } = usePromptStats()
  const { favorites } = useFavorites()
  const { data: collections } = useCollections()
  const { data: popularTags } = useQuery({
    queryKey: ['prompts', 'popularTags'],
    queryFn: () => fetchPopularTags(100),
    staleTime: 5 * 60_000,
  })

  const categories = getAllCategories()
  const categoryOptions: CategoryOption[] = useMemo(
    () => [
      { value: 'all', label: 'Toutes' },
      ...categories.map(({ key, accent }) => ({
        value: key,
        label: accent.label,
        color: accent.color,
        icon: accent.icon,
      })),
    ],
    [categories],
  )

  // Intentions = combos search+catégorie (action). On reprend l'accent de la catégorie ciblée.
  const intentOptions: CategoryOption[] = useMemo(
    () => [
      { value: '', label: 'Choisir une intention…', icon: IconBolt },
      ...PRESETS.map((p) => {
        const accent = getAccent(p.category)
        return { value: p.label, label: p.label, color: accent.color, icon: accent.icon }
      }),
    ],
    [],
  )

  // Current filter values from URL (only meaningful on /browse)
  const isBrowse = location.pathname === '/browse'
  const activeCategory = searchParams.get('category') ?? 'all'
  const activeSort = searchParams.get('sort') ?? 'relevance'
  const activeAiModel = searchParams.get('aiModel') ?? 'all'
  const activeSearch = searchParams.get('search') ?? ''
  const activeTag = searchParams.get('tag') ?? ''
  const activeCategoryAccent = getAccent(activeCategory)
  // Intention « active » si les filtres courants correspondent exactement à un preset.
  const activeIntent =
    PRESETS.find((p) => p.search === activeSearch && p.category === activeCategory)?.label ?? ''

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0
    if (activeCategory !== 'all') count++
    if (activeAiModel !== 'all') count++
    if (activeSearch) count++
    if (activeSort !== 'relevance') count++
    if (activeTag) count++
    return count
  }, [activeCategory, activeAiModel, activeSearch, activeSort, activeTag])

  // Navigate to /browse with updated params
  const setFilter = useCallback(
    (key: string, value: string) => {
      const params = new URLSearchParams(searchParams)
      if (value === 'all' || value === '' || value === 'relevance') {
        params.delete(key)
      } else {
        params.set(key, value)
      }
      const qs = params.toString()
      navigate(`/browse${qs ? `?${qs}` : ''}`, { replace: isBrowse })
    },
    [searchParams, navigate, isBrowse],
  )

  const applyPreset = useCallback(
    (preset: (typeof PRESETS)[number]) => {
      const params = new URLSearchParams()
      params.set('search', preset.search)
      params.set('category', preset.category)
      navigate(`/browse?${params.toString()}`)
    },
    [navigate],
  )

  const clearFilters = useCallback(() => {
    navigate('/browse')
  }, [navigate])

  return (
    <div className="flex h-full flex-col">
      {/* ─── Switcher global entre les apps NeuroForge (haut de sidebar) ─── */}
      {/* z-50 : au-dessus des sections de filtres (z-40 → z-10) dont les
          dropdowns CategorySelect s'ouvrent plus bas dans la sidebar. */}
      <div className="border-(--border) relative z-50 border-b px-3 py-2">
        <AppSwitcher current="prompts" />
      </div>

      {/* ─── Hero card (branding + metrics) ─── */}
      <div className="border-(--border) border-b p-3">
        <div className="relative overflow-hidden rounded-[28px] px-2 pt-2">
          <div className="relative z-10">
            <div className="mb-4 flex items-center gap-3">
              <NavLink to="/" className="flex shrink-0 items-center justify-center">
                <Logo size={88} />
              </NavLink>
              <div>
                <p className="text-(--accent) text-[10px] font-extrabold uppercase tracking-[0.24em]">
                  NeuroForge
                </p>
                <h1 className="text-lg font-extrabold tracking-tight">Prompt Library</h1>
              </div>
            </div>
            <p className="text-(--muted) mb-4 text-sm leading-relaxed">
              Prompts et agents prêts à l'emploi
            </p>
          </div>
          {/* Wave SVG overlay */}
          <div className="leading-0 pointer-events-none absolute inset-x-0 bottom-0">
            <WaveSVG className="h-[50px] w-full opacity-60" />
          </div>
        </div>
      </div>

      {/* ─── Filter sections (scrollable) ─── */}
      <div className="flex-1 space-y-4 overflow-y-auto p-5">
        {/* Nav links (Accueil / Favoris) */}
        <section className="">
          <NavLink
            to="/"
            className={cn(
              'flex items-center gap-3 rounded-2xl px-4 py-2.5 text-sm transition-all',
              location.pathname === '/'
                ? 'text-(--accent) bg-(--accent-subtle)'
                : 'text-(--text-secondary) hover:bg-(--bg-elevated)',
            )}
          >
            <AppIcon icon={IconHome} size={20} aria-hidden /> Accueil
          </NavLink>
          <NavLink
            to="/browse"
            className={cn(
              'flex items-center gap-3 rounded-2xl px-4 py-2.5 text-sm transition-all',
              isBrowse
                ? 'text-(--accent) bg-(--accent-subtle) font-semibold'
                : 'text-(--text-secondary) hover:bg-(--bg-elevated)',
            )}
          >
            <AppIcon icon={IconSearch} size={20} aria-hidden /> Explorer
            <span className="text-(--muted) bg-(--bg-elevated) ml-auto rounded-full px-2 py-0.5 text-[10px] font-bold tabular-nums">
              {total}
            </span>
          </NavLink>
          {isAuthenticated && (
            <>
              <NavLink
                to="/favorites"
                className={cn(
                  'flex items-center gap-3 rounded-2xl px-4 py-2.5 text-sm transition-all',
                  location.pathname === '/favorites'
                    ? 'text-(--accent) bg-(--accent-subtle) font-semibold'
                    : 'text-(--text-secondary) hover:bg-(--bg-elevated)',
                )}
              >
                <AppIcon icon={IconStar} size={20} aria-hidden /> Mes Favoris
                {favorites.length > 0 && (
                  <span className="text-(--muted) bg-(--bg-elevated) ml-auto rounded-full px-2 py-0.5 text-[10px] font-bold tabular-nums">
                    {favorites.length}
                  </span>
                )}
              </NavLink>
              <NavLink
                to="/collections"
                className={cn(
                  'flex items-center gap-3 rounded-2xl px-4 py-2.5 text-sm transition-all',
                  location.pathname.startsWith('/collections')
                    ? 'text-(--accent) bg-(--accent-subtle) font-semibold'
                    : 'text-(--text-secondary) hover:bg-(--bg-elevated)',
                )}
              >
                <AppIcon icon={IconPackage} size={20} aria-hidden /> Mes Collections
                {collections && collections.length > 0 && (
                  <span className="text-(--muted) bg-(--bg-elevated) ml-auto rounded-full px-2 py-0.5 text-[10px] font-bold tabular-nums">
                    {collections.length}
                  </span>
                )}
              </NavLink>
              <NavLink
                to="/my-prompts"
                className={cn(
                  'flex items-center gap-3 rounded-2xl px-4 py-2.5 text-sm transition-all',
                  location.pathname === '/my-prompts'
                    ? 'text-(--accent) bg-(--accent-subtle) font-semibold'
                    : 'text-(--text-secondary) hover:bg-(--bg-elevated)',
                )}
              >
                <AppIcon icon={IconPencil} size={20} aria-hidden /> Mes Prompts
              </NavLink>
              <NavLink
                to="/my-prompts/new"
                className={cn(
                  'flex items-center gap-3 rounded-2xl px-4 py-2.5 text-sm transition-all',
                  location.pathname === '/my-prompts/new'
                    ? 'text-(--accent) bg-(--accent-subtle) font-semibold'
                    : 'text-(--text-secondary) hover:bg-(--bg-elevated)',
                )}
              >
                <AppIcon icon={IconFilePlus} size={20} aria-hidden /> Créer un prompt
              </NavLink>
              <NavLink
                to="/profile"
                className={cn(
                  'flex items-center gap-3 rounded-2xl px-4 py-2.5 text-sm transition-all',
                  location.pathname === '/profile'
                    ? 'text-(--accent) bg-(--accent-subtle) font-semibold'
                    : 'text-(--text-secondary) hover:bg-(--bg-elevated)',
                )}
              >
                <AppIcon icon={IconUser} size={20} aria-hidden /> Mon Profil
              </NavLink>
              {user?.role === 'ADMIN' && (
                <NavLink
                  to="/admin"
                  className={cn(
                    'flex items-center gap-3 rounded-2xl px-4 py-2.5 text-sm transition-all',
                    location.pathname.startsWith('/admin')
                      ? 'bg-red-500/10 font-semibold text-red-500'
                      : 'text-(--text-secondary) hover:bg-(--bg-elevated)',
                  )}
                >
                  <AppIcon icon={IconShield} size={20} aria-hidden /> Admin
                </NavLink>
              )}
            </>
          )}
        </section>

        {/* Batch 4 — Feedback (filtres actifs + reset) */}
        <section className="glass space-y-3 rounded-3xl p-5">
          <div className="flex items-center justify-between gap-3">
            <p className="text-(--accent) text-[10px] font-black uppercase tracking-[0.22em]">
              Filtres actifs
            </p>
            {activeFilterCount > 0 && (
              <button
                onClick={clearFilters}
                className="text-xs font-bold text-[var(--error)] hover:underline"
              >
                Réinitialiser
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {activeSearch && (
              <button
                onClick={() => setFilter('search', '')}
                className="text-(--accent) bg-(--accent-subtle) rounded-xl border border-[var(--accent)] px-3 py-2 text-xs font-bold"
              >
                Recherche : {activeSearch} ×
              </button>
            )}
            {activeTag && (
              <button
                onClick={() => setFilter('tag', '')}
                className="text-(--accent) bg-(--accent-subtle) rounded-xl border border-[var(--accent)] px-3 py-2 text-xs font-bold"
              >
                Tag : #{activeTag} ×
              </button>
            )}
            {activeCategory !== 'all' && (
              <button
                onClick={() => setFilter('category', 'all')}
                className="text-(--accent) bg-(--accent-subtle) rounded-xl border border-[var(--accent)] px-3 py-2 text-xs font-bold"
              >
                Catégorie : {activeCategoryAccent.label} ×
              </button>
            )}
            {activeAiModel !== 'all' && (
              <button
                onClick={() => setFilter('aiModel', 'all')}
                className="text-(--accent) bg-(--accent-subtle) rounded-xl border border-[var(--accent)] px-3 py-2 text-xs font-bold"
              >
                Modèle :{' '}
                {AI_MODEL_LABELS[activeAiModel as keyof typeof AI_MODEL_LABELS]?.label ??
                  activeAiModel}{' '}
                ×
              </button>
            )}
            {activeSort !== 'relevance' && (
              <button
                onClick={() => setFilter('sort', 'relevance')}
                className="text-(--accent) bg-(--accent-subtle) rounded-xl border border-[var(--accent)] px-3 py-2 text-xs font-bold"
              >
                Tri : {SORT_OPTIONS.find((o) => o.value === activeSort)?.label} ×
              </button>
            )}
            {activeFilterCount === 0 && (
              <span className="text-(--muted) text-xs">Aucun filtre actif</span>
            )}
          </div>
          <div className="border-(--border) rounded-2xl border border-dashed p-4">
            <p className="text-(--muted) text-sm">
              {isBrowse ? 'Filtrage en direct' : 'Sélectionne un filtre pour explorer'}
            </p>
          </div>
        </section>

        {/* Tri */}
        <section className="glass relative z-40 space-y-3 rounded-3xl p-5">
          <p className="text-(--accent) text-[10px] font-black uppercase tracking-[0.22em]">Tri</p>
          <CategorySelect
            options={SORT_OPTIONS}
            value={activeSort}
            onChange={(val) => setFilter('sort', val)}
          />
        </section>

        {/* Catégorie */}
        <section className="glass relative z-30 space-y-3 rounded-3xl p-5">
          <p className="text-(--accent) text-[10px] font-black uppercase tracking-[0.22em]">
            Catégorie
          </p>
          <CategorySelect
            options={categoryOptions}
            value={activeCategory}
            onChange={(val) => setFilter('category', val)}
          />
        </section>

        {/* Intentions / Presets */}
        <section className="glass relative z-20 space-y-3 rounded-3xl p-5">
          <p className="text-(--accent) text-[10px] font-black uppercase tracking-[0.22em]">
            Intentions
          </p>
          <CategorySelect
            options={intentOptions}
            value={activeIntent}
            onChange={(val) => {
              const preset = PRESETS.find((p) => p.label === val)
              if (preset) applyPreset(preset)
            }}
          />
        </section>

        {/* Modèle IA
        <section className="glass space-y-3 rounded-3xl p-5">
          <p className="text-(--accent) text-[10px] font-black uppercase tracking-[0.22em]">
            Modèle IA
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilter('aiModel', 'all')}
              className={cn(
                'rounded-xl border px-3 py-2 text-xs font-bold transition-all duration-150',
                activeAiModel === 'all'
                  ? 'text-(--accent) bg-(--accent-subtle) border-[var(--accent)]'
                  : 'border-(--border) text-(--text-secondary) bg-[var(--bg-soft)] hover:border-[var(--accent)]',
              )}
            >
              Tous
            </button>
            {Object.entries(AI_MODEL_LABELS).map(([key, { label, icon }]) => (
              <button
                key={key}
                onClick={() => setFilter('aiModel', key)}
                className={cn(
                  'rounded-xl border px-3 py-2 text-xs font-bold transition-all duration-150',
                  activeAiModel === key
                    ? 'text-(--accent) bg-(--accent-subtle) border-[var(--accent)]'
                    : 'border-(--border) text-(--text-secondary) bg-[var(--bg-soft)] hover:border-[var(--accent)]',
                )}
              >
                {icon} {label}
              </button>
            ))}
          </div>
        </section>

        */}

        {/* Tags — recherche typeahead (alimente le filtre ?tag=...) */}
        {popularTags && popularTags.length > 0 && (
          <section className="glass relative z-10 space-y-3 rounded-3xl p-5">
            <p className="text-(--accent) text-[10px] font-black uppercase tracking-[0.22em]">
              Tags
            </p>
            <TagTypeahead
              suggestions={popularTags}
              value={activeTag}
              onChange={(tag) => setFilter('tag', tag)}
            />
          </section>
        )}
      </div>
    </div>
  )
}
